import { memo, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { MessageExtendComplex } from '../../MessageExtend/MessageExtendComplex';
import { BaseMsgProps } from '../types';
import type { DialogueJolectAttachmentMessage } from 'components/Dialogue/types';
import { formatFileSize } from 'utils/file';
import { openExternalURL } from 'utils/openExternalURL';
import { JolectFileIcon } from './JolectFileIcon';
import { ReactComponent as LinkIcon } from './assets/link.svg';

const getFileExt = (filename?: string) => {
  if (!filename) {
    return '';
  }
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex >= 0 ? filename.substring(dotIndex) : filename;
};

export const JolectAttachmentMsg = memo((props: BaseMsgProps) => {
  const { msg } = props;
  const { t } = useTranslate();
  const { content } = msg as DialogueJolectAttachmentMessage;
  const { url, name, size, mimeType } = content || {};

  const ext = useMemo(() => {
    const derived = getFileExt(name);
    if (derived) {
      return derived;
    }
    if (mimeType) {
      const map: Record<string, string> = {
        'application/pdf': '.pdf',
        'text/plain': '.txt',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      };
      return map[mimeType] || '';
    }
    return '';
  }, [name, mimeType]);

  const fileSize = useMemo(() => {
    const numericSize = Number(size);
    return Number.isNaN(numericSize) ? undefined : numericSize;
  }, [size]);

  if (!content) {
    return null;
  }

  const handleClick = () => {
    if (url) {
      openExternalURL(url, '__blank');
    }
  };

  return (
    <MessageExtendComplex
      {...props}
      showSourceContent={false}
      content={
        <div className="flex flex-col gap-2 p-2 bg-[#E3EDFF] rounded w-[320px]">
          <div className="flex items-center gap-2 h-16 px-2 bg-fill-0 rounded" title={name}>
            <JolectFileIcon ext={ext} className="flex-shrink-0 text-[48px] leading-none" />
            <div className="flex flex-col min-w-0">
              {name ? (
                <div className="text-sm truncate" title={name}>
                  {name}
                </div>
              ) : null}
              <div className="text-xs mt-0.5">{formatFileSize(fileSize)}</div>
            </div>
          </div>
          <div
            className="flex items-center justify-center gap-1 h-8 bg-fill-0 rounded cursor-pointer text-sm hover:bg-fill-100"
            onClick={handleClick}
          >
            <span>{t('scene.chatWorkspace.messageType.jolect.viewFiles')}</span>
            <LinkIcon className="w-4 h-4" />
          </div>
        </div>
      }
    />
  );
});
